import React from "react";
import { Link } from "react-router-dom";
import { Heart, Phone, MessageCircle, MapPin, Mail, Instagram, Youtube, Globe } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useClinic } from "../context/ClinicContext";

const Footer = () => {
  const { t, lang } = useLanguage();
  const { clinic } = useClinic();

  return (
    <footer
      data-testid="site-footer"
      className="mt-20 border-t border-forest/10 bg-cream/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <h3 className="font-heading text-2xl text-forest font-semibold mb-3">
            {lang === "ar" ? "العلاج والرعاية الإسلامية" : "Islamic Cure & Care"}
          </h3>
          <p className="arabic text-gold text-lg mb-3">طب النبوي ٢٠٢٦</p>
          <p className="text-sm text-mute leading-relaxed mb-5">{t("footer_intro")}</p>

          {clinic && (
            <div className="space-y-2 text-sm text-mute">
              <a
                href={`tel:${clinic.phone_landline}`}
                data-testid="footer-phone"
                className="flex items-center gap-2 hover:text-forest transition"
              >
                <Phone className="w-3.5 h-3.5 text-gold" />
                <bdi dir="ltr">{clinic.phone_landline_display}</bdi>
              </a>
              <a
                href={`https://wa.me/${clinic.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp"
                className="flex items-center gap-2 hover:text-forest transition"
              >
                <MessageCircle className="w-3.5 h-3.5 text-gold" />
                <span>WhatsApp <bdi dir="ltr">{clinic.phone_mobile_display}</bdi></span>
              </a>
              <a
                href={`mailto:${clinic.email}`}
                data-testid="footer-email"
                className="flex items-center gap-2 hover:text-forest transition break-all"
              >
                <Mail className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <bdi dir="ltr">{clinic.email}</bdi>
              </a>
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-gold flex-shrink-0 mt-1" />
                <bdi dir="ltr">{clinic.address.full}</bdi>
              </div>
              <div className="flex items-center gap-3 pt-3">
                <a href={clinic.website} target="_blank" rel="noreferrer" data-testid="footer-website" className="text-gold hover:text-forest">
                  <Globe className="w-4 h-4" />
                </a>
                <a href={clinic.social.instagram} target="_blank" rel="noreferrer" className="text-gold hover:text-forest">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href={clinic.social.youtube} target="_blank" rel="noreferrer" className="text-gold hover:text-forest">
                  <Youtube className="w-4 h-4" />
                </a>
              </div>
            </div>
          )}
        </div>

        <div>
          <h4 className="font-heading text-lg text-forest font-semibold mb-3">
            {t("footer_explore")}
          </h4>
          <ul className="space-y-2 text-sm text-mute">
            <li><Link to="/" className="hover:text-forest transition" data-testid="footer-home">{t("home")}</Link></li>
            <li><Link to="/search" className="hover:text-forest transition" data-testid="footer-search">{t("ai_search")}</Link></li>
            <li><Link to="/hijama-atlas" className="hover:text-forest transition" data-testid="footer-hijama">{t("hijama_atlas")}</Link></li>
            <li><Link to="/ruqyah" className="hover:text-forest transition" data-testid="footer-ruqyah">{t("ruqyah")}</Link></li>
            <li><Link to="/services" className="hover:text-forest transition" data-testid="footer-services">{t("services")}</Link></li>
            <li><Link to="/products" className="hover:text-forest transition" data-testid="footer-products">{t("products")}</Link></li>
            <li><Link to="/contact" className="hover:text-forest transition" data-testid="footer-contact">{t("contact")}</Link></li>
            <li><Link to="/about" className="hover:text-forest transition" data-testid="footer-about">{t("about")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-lg text-forest font-semibold mb-3">
            {t("footer_disclaimer_title")}
          </h4>
          <p className="text-xs text-mute leading-relaxed">
            {t("footer_disclaimer_body_1")}{" "}
            <strong className="text-clay">{t("footer_disclaimer_body_2")}</strong>
            {t("footer_disclaimer_body_3")}
          </p>
        </div>
      </div>

      <div className="border-t border-forest/10 px-4 sm:px-8 py-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-mute">
          <p>{t("footer_copyright")}</p>
          <p className="flex items-center gap-2">
            {t("footer_made_with")} <Heart className="w-3.5 h-3.5 text-clay" /> {t("footer_for_ummah")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
